import React, { useContext } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate, useParams } from "react-router-dom";

import { messages } from "./messages";
import { Card, CircleIconContainer, CurrencyPrice } from "@letsbit/milkylb";
import { useThemeToBody } from "hooks/utils/useThemeToBody";
import { useFormatter } from "hooks/utils/useFormatter";
import { FormatedValue } from "components/FormatedValue";
import { useMemberPlatformBalancePage } from "./useMemberPlatformBalancePage";
import { BackButton } from "components/BackButton";
import { PermissionsContext } from "providers/PermissionProvider";
import { READ_PERMISSIONS } from "components/RouteWithPermission";
import { BalanceCard } from "components/BalanceCard";
import LoadingAnimation from "components/LoadingAnimation";

export const MemberPlatformBalancePage: React.FC = () => {
  const navigate = useNavigate();
  const { getThemeUrlIcon } = useThemeToBody();
  const { formatMessage } = useIntl();
  const { formatValue } = useFormatter();

  const { uid } = useParams<{ uid: string }>();

  const {
    wallets,
    fiatWallets,
    otherWallets,
    total,
    preferredCurrency,
    name,
    isLoading,
  } = useMemberPlatformBalancePage(uid);
  const { permission } = useContext(PermissionsContext);

  return isLoading ? (
    <LoadingAnimation />
  ) : (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <div className="mb-2 flex flex-row items-center justify-between border-b border-[--milkylb-color-secondary-100]">
        <div className=" mb-8 flex flex-col items-start justify-normal">
          <BackButton className="mb-4" />
          <h1 className="font-['Tomato_Grotesk'] text-4xl font-bold">
            <FormattedMessage {...messages.title} values={{ name }} />
          </h1>
        </div>
        {preferredCurrency && wallets?.[preferredCurrency] && (
          <div className=" mb-8 flex flex-col items-end justify-normal">
            <label
              onClick={() => navigate(-1)}
              className="flex items-center text-sm font-normal text-[--milkylb-color-neutral-400]"
            >
              <FormattedMessage {...messages.totalBalance} />
            </label>
            <h1 className="font-['Tomato_Grotesk'] text-2xl font-bold text-[--milkylb-color-neutral-600]">
              <FormatedValue
                value={total.toString()}
                precision={wallets?.[preferredCurrency].precision}
              />{" "}
              {wallets?.[preferredCurrency].ticker_id.toUpperCase()}
            </h1>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2 overflow-auto p-2">
        <div className="-m-2 flex shrink-0 grow flex-row gap-6 overflow-auto p-2">
          {fiatWallets.map((wallet) => (
            <BalanceCard
              wallet={wallet}
              className="fadeIn w-[320px] grow flex-nowrap"
              onClick={() =>
                navigate(`/logged/members/${uid}/platform/balance/${wallet.id}`)
              }
            />
          ))}
        </div>
        {otherWallets.length > 0 && (
          <h1 className="mt-2 font-['Tomato_Grotesk'] text-2xl font-bold text-[--milkylb-color-neutral-800]">
            <FormattedMessage {...messages.otherCurrencies} />
          </h1>
        )}
        {otherWallets.map((wallet) => {
          const CardItem = (
            <Card
              key={wallet.id}
              title={wallet.ticker_id.toUpperCase() || ""}
              subtitle={
                <div className="flex text-xs font-semibold text-[--milkylb-color-neutral-400]">
                  <p>{wallet.name}</p>
                </div>
              }
              icon={
                <CircleIconContainer icon={getThemeUrlIcon(wallet.icon_url)} />
              }
            >
              <div className="flex gap-4">
                <CurrencyPrice
                  price={formatValue(
                    wallet.balance === "" ? "0" : wallet.balance,
                    wallet.precision,
                  )}
                  priceCurrency={""}
                  variation={formatMessage(messages.inAccount)}
                />
              </div>
            </Card>
          );

          return READ_PERMISSIONS.includes(permission) ? (
            <Link to={`/logged/members/${uid}/platform/balance/${wallet.id}`}>
              {CardItem}
            </Link>
          ) : (
            CardItem
          );
        })}
      </div>
    </div>
  );
};
